import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import DropZone from "../components/DropZone";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

export default function Convert() {
  const { t, i18n } = useTranslation();
  const isES = i18n.language === "es";
  const [formats, setFormats] = useState([]);
  const [files, setFiles] = useState([]); // multiple
  const [target, setTarget] = useState("pdf");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/convert/formats").then(({ data }) => setFormats(data.formats)).catch(() => {});
  }, []);

  async function onConvert() {
    if (files.length === 0) {
      toast.error(t("cta_choose_file"));
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("target", target);
      const isBatch = files.length > 1;
      if (isBatch) {
        files.forEach((f) => fd.append("files", f));
      } else {
        fd.append("file", files[0]);
      }
      const url = isBatch ? "/convert/batch" : "/convert";
      const { data } = await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      setResult(data);
      toast.success(t("convert_done"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-12" data-testid="convert-page">
      <span className="eyebrow">03 · {t("nav_convert")}</span>
      <h1 className="display text-4xl sm:text-6xl mt-3">{t("convert_title")}</h1>
      <p className="text-[#57534E] mt-3 max-w-2xl font-light">
        {isES
          ? "Sube uno o varios archivos y elige el formato de destino. Sin límite de cantidad."
          : "Upload one or many files and pick the target format. No file count limit."}
      </p>

      <div className="mt-10">
        <DropZone
          multiple
          onFiles={(fs) => setFiles((prev) => [...prev, ...fs])}
          testId="convert-dropzone"
          label={
            files.length === 0
              ? (isES ? "Suelta tus archivos aquí" : "Drop your files here")
              : (isES
                  ? `Añadir más archivos (${files.length} en cola)`
                  : `Add more files (${files.length} queued)`)
          }
        />

        {files.length > 0 && (
          <ul className="mt-6 grid gap-2" data-testid="convert-file-list">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`}
                className="flex items-center justify-between border-2 border-[#1C1917] bg-white px-4 py-3 shadow-[3px_3px_0_0_#1C1917]">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="mono text-[10px] uppercase font-bold tracking-wider text-[#57534E] w-12 shrink-0">
                    {(f.name.split(".").pop() || "?").toUpperCase()}
                  </span>
                  <span className="text-sm font-medium truncate">{f.name}</span>
                  <span className="text-xs text-[#57534E] shrink-0">{(f.size / 1024).toFixed(1)} KB</span>
                </div>
                <button
                  className="text-xs text-[#E5484D] hover:underline ml-3 shrink-0 font-bold"
                  onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                  data-testid={`convert-remove-${i}`}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}

        {files.length > 0 && (
          <div className="mt-6 border-2 border-[#1C1917] shadow-[4px_4px_0_0_#1C1917] p-6 bg-white">
            <div className="grid sm:grid-cols-3 gap-4 items-end">
              <div className="sm:col-span-2">
                <span className="eyebrow">{t("target_format")}</span>
                <div className="mt-2">
                  <Select value={target} onValueChange={setTarget}>
                    <SelectTrigger className="border-2 border-[#1C1917] h-11 rounded-none font-medium" data-testid="convert-target-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-2 border-[#1C1917]">
                      {formats.map((f) => (
                        <SelectItem key={f} value={f} data-testid={`format-${f}`}>
                          {f.toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                onClick={onConvert}
                disabled={busy}
                className="btn-primary rounded-none h-11 px-6"
                data-testid="convert-submit"
              >
                {busy
                  ? t("processing")
                  : files.length > 1
                    ? (isES ? `Convertir ${files.length} archivos →` : `Convert ${files.length} files →`)
                    : `${t("cta_convert")} →`}
              </Button>
            </div>
            {files.length > 1 && (
              <div className="mt-4 text-xs text-[#57534E]">
                {isES
                  ? "Recibirás un .zip con todas las conversiones. Los archivos que fallen se listarán en _errors.txt dentro del zip."
                  : "You'll get a .zip with every conversion. Failed files are listed in _errors.txt inside the zip."}
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="mt-6 border-2 border-[#1C1917] shadow-[6px_6px_0_0_#1C1917] p-6 bg-[#FFC53D]/20 fade-up" data-testid="convert-result">
            <span className="sticker" style={{ background: "#3E63DD", color: "#FFFFFF" }}>{t("convert_done")}</span>
            <div className="display text-2xl mt-3">{result.original_filename}</div>
            <div className="text-[#57534E] text-sm mt-1 mono">{result.operation}</div>
            {result.log && result.log.length > 0 && (
              <ul className="mt-3 grid gap-1 mono text-xs text-[#57534E]">
                {result.log.map((l, i) => <li key={i}>{l}</li>)}
              </ul>
            )}
            <div className="mt-5 flex gap-3">
              <Button
                onClick={() => downloadFile(result.id, result.original_filename)}
                className="btn-primary rounded-none h-11 px-6"
                data-testid="convert-download-btn"
              >
                ↓ {t("cta_download")}
              </Button>
              <Button variant="ghost"
                className="rounded-none border-2 border-[#1C1917] hover:bg-[#FFC53D] h-11 font-bold"
                onClick={() => { setFiles([]); setResult(null); }}
                data-testid="convert-clear-btn">
                {t("cta_clear")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
